import { faUmbrellaBeach, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useState } from 'react'
import { SUPPORT_HOLIDAY_NOTICE } from '../constants'
import AnimateHeight from './AnimateHeight'

/**
 * A slim, low-key notice at the very top of the app chrome — quieter than the
 * KYC/compliance banners on purpose, since it's informational, not a call to
 * action. It carries a dismiss button, and the dismissal sticks (per notice
 * window) so it doesn't nag on every navigation or reload.
 */
interface SupportNoticeBannerProps {
  /** Whether the notice is in-window and the current user should see it. */
  isVisible: boolean
}

// Scope the "dismissed" flag to the active window so a future summer's notice
// (a different date range) surfaces again instead of staying hidden forever.
const DISMISS_STORAGE_KEY = `support-holiday-notice-dismissed:${SUPPORT_HOLIDAY_NOTICE.startsOn}_${SUPPORT_HOLIDAY_NOTICE.endsOn}`

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === 'true'
  } catch {
    // Private-mode / storage-disabled: fall back to showing the notice.
    return false
  }
}

const SupportNoticeBanner: React.FC<SupportNoticeBannerProps> = ({
  isVisible,
}) => {
  const [dismissed, setDismissed] = useState(readDismissed)

  const onDismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, 'true')
    } catch {
      // Non-persistent dismissal is fine — it still hides for this session.
    }
  }

  return (
    <AnimateHeight isVisible={isVisible && !dismissed} durationInS={0.25}>
      <div className="flex items-center gap-2.5 border-b border-border-base bg-bg-component px-4 py-1.5 text-fg-base dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
        <FontAwesomeIcon
          icon={faUmbrellaBeach}
          className="shrink-0 text-xs text-fg-subtle dark:text-gray-400"
        />
        <p className="min-w-0 flex-1 text-xs font-medium">
          {SUPPORT_HOLIDAY_NOTICE.message}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notice"
          className="shrink-0 rounded p-1 text-fg-subtle transition-colors hover:text-fg-base dark:text-gray-400 dark:hover:text-gray-100"
        >
          <FontAwesomeIcon icon={faXmark} className="text-xs" />
        </button>
      </div>
    </AnimateHeight>
  )
}

export default SupportNoticeBanner
